import type { components, paths } from "admination-react/api-schema";
import {
  getJson,
  send,
  sendJson,
  setActiveOrganisationId,
  setAuthenticatedUser,
  setAuthenticatedSession,
  setCsrfToken,
  trackCsrfRefresh,
  ApiError,
  captureWriteIdentity,
  assertWriteIdentity,
  rotateCsrfToken,
  beginAuthenticationUpdate,
  publishAuthentication,
  type WriteIdentity,
} from "./http";

export type Variant = components["schemas"]["VariantRead"];
export type Me = components["schemas"]["SessionRead"];
export type LinkSession = NonNullable<
  paths["/auth/link"]["post"]["responses"][200]["content"]["application/json"]
>;

export async function fetchMe(): Promise<Me | null> {
  const update = beginAuthenticationUpdate();
  return trackCsrfRefresh(
    getJson<Me>("/auth/me").then((answer) => {
      setAuthenticatedSession(answer, update);
      return answer;
    }).catch((error: unknown) => {
      if (error instanceof ApiError && error.status === 401) {
        publishAuthentication(update, () => {
          setAuthenticatedUser(null);
          setCsrfToken(null);
        });
        return null;
      }
      throw error;
    }), update
  );
}

export async function switchVariant(
  organisationId: string,
  variantKey: string
): Promise<WriteIdentity> {
  const body: components["schemas"]["VariantSwitchIn"] = {
    organisation_id: organisationId,
    variant_key: variantKey,
  };
  const update = beginAuthenticationUpdate("variant");
  const session = await sendJson<Me>("/auth/me/variant", "POST", body);
  setAuthenticatedSession(session, update);
  return captureWriteIdentity();
}

export async function signOut(): Promise<WriteIdentity> {
  const identity = captureWriteIdentity();
  await rotateCsrfToken();
  const update = beginAuthenticationUpdate("session");
  await send<void>("/auth/logout", { method: "POST", identity });
  publishAuthentication(update, () => {
    setAuthenticatedUser(null);
    setCsrfToken(null);
    setActiveOrganisationId(null);
  });
  return captureWriteIdentity();
}

export async function redeemLinkToken(token: string): Promise<LinkSession> {
  const update = beginAuthenticationUpdate("session");
  const identity = captureWriteIdentity();
  const session = await sendJson<LinkSession>("/auth/link", "POST", { token });
  assertWriteIdentity(identity);
  publishAuthentication(update, () => {
    setAuthenticatedUser(null);
    setCsrfToken(session.csrf_token ?? null);
  });
  return session;
}

export async function registerWithEmail(email: string): Promise<void> {
  await sendJson<void>("/auth/register", "POST", { email });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await sendJson<void>("/auth/passwort/vergessen", "POST", { email });
}

export async function signInWithPassword(email: string, password: string): Promise<void> {
  const update = beginAuthenticationUpdate("session");
  const identity = captureWriteIdentity();
  const session = await sendJson<components["schemas"]["StaffLoginRead"]>(
    "/auth/login",
    "POST",
    { email, password }
  );
  assertWriteIdentity(identity);
  publishAuthentication(update, () => {
    setAuthenticatedUser(null);
    setCsrfToken(session.csrf_token);
  });
}

export async function redeemRegistrationLink(token: string): Promise<components["schemas"]["StaffLoginRead"]> {
  const update = beginAuthenticationUpdate("session");
  const identity = captureWriteIdentity();
  const session = await sendJson<components["schemas"]["StaffLoginRead"]>(
    "/auth/registration-link", "POST", { token }
  );
  assertWriteIdentity(identity);
  publishAuthentication(update, () => {
    setAuthenticatedUser(null);
    setCsrfToken(session.csrf_token);
  });
  return session;
}

export async function resetPassword(token: string, passwort: string): Promise<void> {
  await sendJson<void>("/auth/passwort/zuruecksetzen", "POST", { token, passwort });
}

/** 204 when `passwort` is the signed-in user's own; 400 when not, 429 when locked. */
export function verifyPassword(passwort: string): Promise<void> {
  const body: components["schemas"]["PasswortPruefenIn"] = { passwort };
  return sendJson<void>("/auth/passwort/pruefen", "POST", body);
}

export async function changePassword(passwortAlt: string, passwort: string): Promise<void> {
  const identity = captureWriteIdentity();
  await rotateCsrfToken();
  await sendJson<void>("/auth/passwort/aendern", "POST", {
    passwort_alt: passwortAlt,
    passwort,
  }, undefined, identity);
}
